import { createFileRoute } from "@tanstack/react-router";
import { SeoPageShell, H2 } from "@/components/SeoPageShell";

const TITLE = "How Like2Fit Works — Find Clothes From a Picture with AI";
const DESC = "See how the Like2Fit AI outfit finder identifies every item in a photo and matches it to shoppable products at your budget in seconds.";
const URL = "https://like2fit.com/how-it-works";

export const Route = createFileRoute("/how-it-works")({
  component: HowItWorksPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to find clothes from a picture with Like2Fit",
          description: DESC,
          step: [
            { "@type": "HowToStep", name: "Upload an outfit photo", text: "Drag in a screenshot or photo of any outfit you love — Instagram, TikTok, Pinterest or a street-style shot." },
            { "@type": "HowToStep", name: "AI identifies every item", text: "Our AI detects each clothing piece in the photo — tops, bottoms, shoes and accessories." },
            { "@type": "HowToStep", name: "Get shoppable matches", text: "We surface exact items and close alternatives across hundreds of retailers, filtered to your budget." },
            { "@type": "HowToStep", name: "Shop the look", text: "Click through to buy, or save the outfit to your wardrobe for later." },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://like2fit.com/" },
            { "@type": "ListItem", position: 2, name: "How it works", item: URL },
          ],
        }),
      },
    ],
  }),
});

function HowItWorksPage() {
  return (
    <SeoPageShell
      eyebrow="How it works"
      h1="Find clothes from a picture in seconds"
      intro="Like2Fit is an AI outfit finder built for shoppers. Upload any outfit photo and we identify every item, then match it to real products you can buy — at the budget you set."
    >
      <H2>1. Upload an outfit photo</H2>
      <p>Drop in a screenshot from Instagram, TikTok, Pinterest or a street-style snap. Anything with a clear view of the outfit works — full-body, half-body, even flat-lays.</p>

      <H2>2. AI identifies every item</H2>
      <p>Our computer-vision model detects every clothing piece in the image — tops, bottoms, jackets, shoes, bags and accessories — and figures out the colour, material and silhouette of each one.</p>

      <H2>3. Get shoppable matches at your budget</H2>
      <p>We search hundreds of retailers in real time and surface exact matches plus close alternatives across budget, mid and premium tiers. You only see options that fit the price range you set.</p>

      <H2>4. Shop the look or save it</H2>
      <p>Tap any item to buy from the retailer. Save the outfit to your wardrobe to revisit later, or use Style My Wardrobe to pair the new pieces with clothes you already own.</p>

      <H2>Why use an AI outfit finder?</H2>
      <p>Manually reverse-searching every item in an outfit is slow — and Google image search rarely surfaces the actual product. An AI outfit finder reads the image the way a stylist would and shops it for you, in seconds.</p>
    </SeoPageShell>
  );
}
